import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { Cross, Phone, MapPin, Clock, Calendar, AlertCircle, Map as MapIcon } from "lucide-react";

const TR_DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

function isoDate(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}
function prettyDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${TR_MONTHS[d.getMonth()]} ${d.getFullYear()} ${TR_DAYS[d.getDay()]}`;
}
function mapUrl(p, provinceName) {
  // Build the most precise search string we can: pharmacy name + address + district + province
  const parts = [p?.name, p?.address, p?.district, provinceName, "Türkiye"].filter(Boolean);
  const q = encodeURIComponent(parts.join(", "));
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

export default function Pharmacies() {
  const { selected } = useProvince();
  const today = isoDate(0);
  const yesterday = isoDate(-1);
  const tomorrow = isoDate(1);
  const [date, setDate] = useState(today);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selected?.id) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/pharmacies?province_id=${selected.id}&on_duty_date=${date}`);
        if (!cancelled) setItems(r.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selected?.id, date]);

  const shiftLabel = useMemo(() => {
    // Pick the first scraped record's label (they're identical for same date)
    const withLabel = items.find((p) => p.on_duty_label);
    return withLabel?.on_duty_label || "";
  }, [items]);

  const relativeName =
    date === today ? "Bugün" : date === yesterday ? "Dün" : date === tomorrow ? "Yarın" : "Seçili gün";

  const chips = [
    { id: "dun", label: "Dün", val: yesterday },
    { id: "bugun", label: "Bugün", val: today },
    { id: "yarin", label: "Yarın", val: tomorrow },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="pharmacies-page">
      <div className="flex items-center gap-2 mb-1">
        <Cross className="w-7 h-7 text-[#e11d2a]" />
        <h1 className="text-2xl md:text-3xl font-black">Nöbetçi Eczaneler</h1>
      </div>
      <p className="text-gray-500 text-sm mb-4" data-testid="pharmacies-subtitle">
        {selected?.name} • {relativeName} — {prettyDate(date)}
      </p>

      {/* Quick day chips */}
      <div className="flex gap-2 mb-3 flex-wrap" data-testid="pharmacies-quick-chips">
        {chips.map((c) => {
          const active = date === c.val;
          return (
            <button
              key={c.id}
              onClick={() => setDate(c.val)}
              data-testid={`chip-${c.id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                active
                  ? "bg-[#e11d2a] text-white shadow-sm"
                  : "bg-white border border-gray-200 text-gray-700 hover:border-[#e11d2a] hover:text-[#e11d2a]"
              }`}
            >
              {c.label}
              <span className={`ml-1.5 text-[10px] ${active ? "text-white/80" : "text-gray-400"}`}>
                {new Date(c.val + "T00:00:00").getDate()} {TR_MONTHS[new Date(c.val + "T00:00:00").getMonth()].slice(0, 3)}
              </span>
            </button>
          );
        })}
        {/* Custom date input */}
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-full text-sm bg-white"
          data-testid="pharmacies-date-input"
        />
      </div>

      {/* Shift info banner */}
      {shiftLabel && (
        <div
          className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg p-3 mb-4 text-sm"
          data-testid="pharmacies-shift-banner"
        >
          <Clock className="w-4 h-4 text-[#e11d2a] shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-[#0b0b0d]">{shiftLabel}</div>
            <div className="text-xs text-gray-500 mt-0.5">Kaynak: eczaneler.gen.tr</div>
          </div>
        </div>
      )}

      {loading && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="pharmacies-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-lg p-4 animate-pulse h-24" />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div
          className="flex flex-col items-center justify-center bg-white border border-gray-100 rounded-lg p-10 text-center"
          data-testid="pharmacies-empty"
        >
          <AlertCircle className="w-10 h-10 text-gray-300 mb-3" />
          <div className="font-semibold text-gray-700">Bu tarih için nöbetçi eczane kaydı bulunamadı.</div>
          <div className="text-xs text-gray-500 mt-1">
            {selected?.name} ili için veri henüz eklenmemiş olabilir. Yönetici panelinden Otomatik Çek özelliği kullanılabilir.
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="pharmacies-list">
          {items.map((p) => (
            <div
              key={p.id}
              className="bg-white border border-gray-100 rounded-lg p-4 flex items-start justify-between gap-3 hover:border-[#e11d2a]/40 hover:shadow-sm transition-all"
              data-testid={`pharmacy-card-${p.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold truncate">{p.name}</div>
                  {p.district && (
                    <span className="text-[10px] uppercase font-semibold tracking-wide px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                      {p.district}
                    </span>
                  )}
                </div>
                {p.address && (
                  <div className="text-sm text-gray-600 mt-1 flex items-start gap-1">
                    <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span className="leading-snug">{p.address}</span>
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {p.on_duty_hours || "Vardiya saatleri için kontrol ediniz"}
                </div>
              </div>
              {(p.phone || p.address) && (
                <div className="shrink-0 flex flex-col gap-1.5">
                  {p.address && (
                    <a
                      href={mapUrl(p, selected?.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 text-xs font-semibold bg-white border border-gray-200 text-gray-800 rounded-md flex items-center justify-center gap-1 hover:border-[#e11d2a] hover:text-[#e11d2a]"
                      data-testid={`pharmacy-map-${p.id}`}
                      title="Bu adresi haritada aç"
                    >
                      <MapIcon className="w-3 h-3" /> Haritada Göster
                    </a>
                  )}
                  {p.phone && (
                    <a
                      href={`tel:${p.phone}`}
                      className="px-3 py-1.5 text-xs font-semibold bg-[#e11d2a] text-white rounded-md flex items-center justify-center gap-1 hover:bg-[#c01825]"
                      data-testid={`pharmacy-call-${p.id}`}
                    >
                      <Phone className="w-3 h-3" /> Ara
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
